// The "we have updated our Terms and Privacy Policy" card on the home dashboard (#5587), as
// one unit: when it shows, what tapping it does, and what dismissing it means.
//
// The rule about WHETHER the account owes a fresh answer is not here — that is
// `decideConsentRefresh` in shared/src/consent-refresh.ts, which is pure, shared
// with the consent dialog, and separately tested. This file gathers its inputs and
// turns its answer into a card.
//
// Why a card at all: before #5587 the consent screen was asked once, at sign-up,
// and never again, so a change to the Terms or the Privacy Policy reached nobody
// who had already accepted. The owner chose to ask everybody again, gently first
// (this card, for a month) and then with the blocking screen. During the gentle
// month this card is the ONLY thing that asks, so a card that fails to appear is
// the whole feature failing.
//
// Angular-free on purpose — see cs-day-items-notice-host.ts for why.

import { DateTime } from 'luxon';
import { ConsentRefresh, decideConsentRefresh } from '@cf-shared/consent-refresh';
import { UserConsentRecords } from '@cf-shared/user-consent-interface';
import { DayItemsNoticeController, DayItemsNoticeHost, markNoticeHostForCheck } from './cs-day-items-notice-host';

export interface PrivacyUpdateNoticeDeps {
  host: DayItemsNoticeHost;
  /** The signed-in account's consent records, from the cached login result.
   *  Undefined while offline or before the CouchDB login lands. */
  getConsents(): UserConsentRecords | undefined;
  /**
   * True while a coach is managing one of their clients. The screen then shows the
   * CLIENT's data, and the coach's own consent business does not belong on it —
   * the same guard the billing notices take, for the same reason.
   */
  isCoachManaging(): boolean;
  /** Evaluation time, supplied so this stays testable without faking the clock. */
  now(): DateTime;
  /** Opens the consent screen for review (the `openTermsReview` pub-sub event). */
  openTermsReview(): void;
  /**
   * Calls `onChanged` whenever the account's consent records are saved, and hands
   * back the function that stops listening again.
   *
   * Needed because the consent screen is an overlay on top of the dashboard: the
   * person accepts, the cached login result is updated in place, and this
   * component is never re-created — so without this the card would keep asking
   * for an answer that has already been given (reported 2026-09-21).
   */
  watchConsentsChanged(onChanged: () => void): () => void;
  /**
   * Reports that the card was shown, tapped or dismissed, as the `action` of one
   * `consent_update_card` event. NOT called `trackEvent`: the telemetry-lexicon
   * gate reads a string literal handed to anything of that name as an event name,
   * and would then demand a lexicon row per action word.
   */
  trackCardAction(action: 'shown' | 'clicked' | 'dismissed'): void;
}

/**
 * The privacy-policy update card.
 *
 * Cheap by construction: it reads the cached login result and the clock, so it
 * costs nothing on the dashboard of everyone whose answer is current.
 */
export class CsPrivacyUpdateNotice implements DayItemsNoticeController {
  /** Whether this notice has something to say. Read by `decideVisibleNotices`. */
  wantsToShow = false;
  /** Set once the person dismissed it, so it stops asking until the app restarts.
   *
   *  Deliberately in memory only, and deliberately not persisted: the deadline is
   *  what ends the asking, and a stored dismissal would let somebody opt out of
   *  ever seeing it while the blocking screen waits for them a month later. */
  private dismissedThisSession = false;
  private reportedShown = false;
  /** Stops listening for saved consent records; held so `dispose` can call it. */
  private stopWatchingConsents: (() => void) | null = null;

  constructor(private deps: PrivacyUpdateNoticeDeps) {
    this.stopWatchingConsents = deps.watchConsentsChanged(() => {
      if (deps.host.isDestroyed()) return;
      this.refresh();
    });
  }

  /**
   * Re-decides whether the card shows.
   *
   * Only the gentle phase draws a card. Once `decideConsentRefresh` answers
   * `Require`, the consent screen raises itself over everything and a card behind
   * it would be asking for something the person cannot avoid anyway.
   */
  refresh(): void {
    if (this.deps.isCoachManaging() || this.dismissedThisSession) {
      this.hide();
      return;
    }
    const refresh = decideConsentRefresh(this.deps.getConsents(), this.deps.now());
    if (refresh !== ConsentRefresh.Ask) {
      this.hide();
      return;
    }
    if (!this.reportedShown) {
      this.reportedShown = true;
      this.deps.trackCardAction('shown');
    }
    this.wantsToShow = true;
    markNoticeHostForCheck(this.deps.host);
  }

  /** The person wants to read it: open the consent screen, which they can close
   *  again while the gentle phase lasts. */
  onActionClick(): void {
    this.deps.trackCardAction('clicked');
    this.deps.openTermsReview();
  }

  /** Dismissal hides the card for this app run only — see `dismissedThisSession`. */
  onDismissed(): void {
    this.deps.trackCardAction('dismissed');
    this.dismissedThisSession = true;
    this.hide();
  }

  /** Releases the consent-records listener taken in the constructor. */
  dispose(): void {
    this.stopWatchingConsents?.();
    this.stopWatchingConsents = null;
  }

  private hide(): void {
    if (!this.wantsToShow) return;
    this.wantsToShow = false;
    markNoticeHostForCheck(this.deps.host);
  }
}
